// src/pages/AuthPage.jsx
import { useState } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import "./AuthPage.css";

export default function AuthPage() {
  const [mode, setMode]           = useState("login"); // "login" | "register" | "reset"
  const [fullName, setFullName]   = useState("");
  const [email, setEmail]         = useState("");
  const [password, setPassword]   = useState("");
  const [confirm, setConfirm]     = useState("");
  const [error, setError]         = useState("");
  const [message, setMessage]     = useState("");
  const [loading, setLoading]     = useState(false);
  const [showPass, setShowPass]   = useState(false);

  const { login, register, resetPassword } = useAuth();
  const navigate  = useNavigate();
  const location  = useLocation();
  const redirect  = location.state?.from || "/account";

  function friendlyError(code) {
    var map = {
      "auth/user-not-found":       "No account found with this email.",
      "auth/wrong-password":       "Incorrect password. Please try again.",
      "auth/email-already-in-use": "An account with this email already exists.",
      "auth/weak-password":        "Password must be at least 6 characters.",
      "auth/invalid-email":        "Please enter a valid email address.",
      "auth/too-many-requests":    "Too many attempts. Please try again later.",
      "auth/invalid-credential":   "Incorrect email or password.",
    };
    return map[code] || "Something went wrong. Please try again.";
  }

  async function handleSubmit(e) {
    e.preventDefault();
    setError(""); setMessage(""); setLoading(true);
    try {
      if (mode === "reset") {
        await resetPassword(email);
        setMessage("Password reset email sent! Check your inbox.");
        setLoading(false);
        return;
      }
      if (mode === "register") {
        if (!fullName.trim()) { setError("Please enter your full name."); setLoading(false); return; }
        if (password !== confirm) { setError("Passwords do not match."); setLoading(false); return; }
        if (password.length < 6) { setError("Password must be at least 6 characters."); setLoading(false); return; }
        await register(email, password, fullName.trim());
      } else {
        await login(email, password);
      }
      navigate(redirect, { replace: true });
    } catch (err) {
      setError(friendlyError(err.code));
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className="auth-page">
      <div className="auth-card anim-fade-in-up">
        {/* Logo */}
        <Link to="/" className="auth-logo">
          <img src="/logo.jpeg" alt="Circuit Printing Solutions" width="96" height="96" className="auth-logo-img" />
        </Link>

        {/* Tabs */}
        {mode !== "reset" && (
          <div className="auth-tabs">
            <button className={"auth-tab" + (mode === "login" ? " active" : "")}
              onClick={function() { setMode("login"); setError(""); }}>
              Sign In
            </button>
            <button className={"auth-tab" + (mode === "register" ? " active" : "")}
              onClick={function() { setMode("register"); setError(""); }}>
              Create Account
            </button>
          </div>
        )}

        {/* Title */}
        <div className="auth-heading">
          {mode === "login"    && <><h2>Welcome back</h2><p>Sign in to your Circuit Printing account</p></>}
          {mode === "register" && <><h2>Create an account</h2><p>Join thousands of Canadian businesses</p></>}
          {mode === "reset"    && <><h2>Reset Password</h2><p>We'll send a reset link to your email</p></>}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="auth-form">
          {mode === "register" && (
            <div className="auth-field">
              <label>Full Name</label>
              <input type="text" value={fullName} onChange={function(e) { setFullName(e.target.value); }}
                placeholder="John Smith" required autoComplete="name" />
            </div>
          )}

          <div className="auth-field">
            <label>Email Address</label>
            <input type="email" value={email} onChange={function(e) { setEmail(e.target.value); }}
              placeholder="john@company.com" required autoComplete="email" />
          </div>

          {mode !== "reset" && (
            <div className="auth-field">
              <label>
                Password
                {mode === "login" && (
                  <button type="button" className="auth-forgot"
                    onClick={function() { setMode("reset"); setError(""); }}>
                    Forgot password?
                  </button>
                )}
              </label>
              <div className="auth-pass-wrap">
                <input type={showPass ? "text" : "password"} value={password}
                  onChange={function(e) { setPassword(e.target.value); }}
                  placeholder={mode === "register" ? "Min. 6 characters" : "••••••••"}
                  required autoComplete={mode === "register" ? "new-password" : "current-password"} />
                <button type="button" className="auth-pass-toggle"
                  onClick={function() { setShowPass(function(s) { return !s; }); }}>
                  {showPass ? "Hide" : "Show"}
                </button>
              </div>
            </div>
          )}

          {mode === "register" && (
            <div className="auth-field">
              <label>Confirm Password</label>
              <input type={showPass ? "text" : "password"} value={confirm}
                onChange={function(e) { setConfirm(e.target.value); }}
                placeholder="Re-enter password" required autoComplete="new-password" />
            </div>
          )}

          {error   && <div className="auth-error">{error}</div>}
          {message && <div className="auth-success">{message}</div>}

          <button type="submit" className="auth-submit" disabled={loading}>
            {loading ? "Please wait..." :
              mode === "login"    ? "Sign In →" :
              mode === "register" ? "Create Account →" :
              "Send Reset Email →"}
          </button>
        </form>

        {/* Footer links */}
        {mode === "reset" && (
          <button className="auth-back" onClick={function() { setMode("login"); setMessage(""); setError(""); }}>
            ← Back to Sign In
          </button>
        )}

        {mode === "register" && (
          <p className="auth-terms">
            By creating an account you agree to our <Link to="/">Terms of Service</Link> and <Link to="/">Privacy Policy</Link>.
          </p>
        )}
      </div>
    </div>
  );
}
